'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react'
import Link from 'next/link'

import { Button } from '@client/components/ui/button'

import type { ArenaResult } from './types'

interface ArenaResultModalProps {
  revealed: boolean
  result: ArenaResult | null
  onNextPaper: () => void
  uploadHref: string
}

export function ArenaResultModal({ revealed, result, onNextPaper, uploadHref }: ArenaResultModalProps) {
  if (!revealed || !result) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onNextPaper}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="bg-background rounded-2xl p-8 mx-4 max-w-md w-full shadow-2xl border"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center space-y-4">
            {/* Correct/Wrong Result */}
            <div className="flex flex-col items-center gap-2">
              {result.wasCorrect ? (
                <>
                  <CheckCircle2 className="h-16 w-16 text-green-500" />
                  <h3 className="text-3xl font-bold text-green-600 dark:text-green-400">Correct!</h3>
                </>
              ) : (
                <>
                  <XCircle className="h-16 w-16 text-red-500" />
                  <h3 className="text-3xl font-bold text-red-600 dark:text-red-400">Wrong!</h3>
                </>
              )}
            </div>

            <p className="text-muted-foreground text-lg">
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                {result.sideASource === 'human' ? 'A' : 'B'}
              </span>{' '}
              was written by a <span className="font-semibold">human reviewer</span>,{' '}
              <span className="font-semibold text-orange-600 dark:text-orange-400">
                {result.sideASource === 'ai' ? 'A' : 'B'}
              </span>{' '}
              was generated by <span className="font-semibold">Reviewer3</span>.
            </p>

            {/* Navigation Button */}
            <div className="pt-4 space-y-3">
              <Button onClick={onNextPaper} size="lg" className="w-full">
                Next Paper
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-sm text-muted-foreground">
                Want to try on your own paper?{' '}
                <Link href={uploadHref} className="text-primary hover:underline font-medium">
                  Get your review →
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
